// modal-manager.tsx
// Centralized modal rendering with consistent animations

import { AnimatePresence, motion } from 'framer-motion';
import styled from '@emotion/styled';
import { useModalContext } from './modal-context';
import { useSettingsContext } from './settings-context';
import { BillingDetails, billingDetailsApi } from '../../../api';
import { useAuth } from '../../../contexts/auth/auth-context';
import { tablet } from '../../../media';
// import { paymentsApi } from '../../../api/payments/payments';

// Modal components
import EditDetailsModal from './billing/modals/edit-details/edit-details';
import CheckoutModal from './billing/modals/checkout/checkout';
import SubscribeNowModal from './billing/modals/subscribe-now/subscribe-now';
import SubscriptionSuccessModal from './billing/modals/subscription-success/subscription-success';
// import PaymentMethodModal from './billing/modals/payment-method/payment-method';

// ─────────────────────────────────────────────────────────────────────────────
// Animation Variants
// ─────────────────────────────────────────────────────────────────────────────

const slideVariants = {
  initial: (direction: 'forward' | 'backward') => ({
    x: direction === 'forward' ? '100%' : '-100%',
  }),
  animate: {
    x: 0,
    transition: {
      duration: 0.3,
    },
  },
  exit: (direction: 'forward' | 'backward') => ({
    x: direction === 'forward' ? '-100%' : '100%',
    transition: {
      duration: 0.3,
    },
  }),
};

const ModalWrapper = styled(motion.div)`
  will-change: transform, opacity;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: #1a1a1c;

  ${tablet`
    position: fixed;
    inset: 0;
    left: var(--sidebar-width, 0px);
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
  `}
`;

const ModalContent = styled.div`
  width: 100%;
  height: 100%;

  ${tablet`
    max-width: 480px;
    max-height: 90vh;
    border-radius: 16px;
    background: #1A1A1C;
    overflow: auto;
    height: auto;
  `}
`;

// ─────────────────────────────────────────────────────────────────────────────
// Modal Manager Component
// ─────────────────────────────────────────────────────────────────────────────

export function ModalManager() {
  const { profile } = useAuth();
  const {
    currentModal,
    direction,
    editCheckoutFlow,
    selectedBillingCycle,
    selectedPlanName,
    openModal,
    closeModal,
    goBack,
  } = useModalContext();

  const { billingDetails, planName, trialEnd, updateBillingDetails, refetchPaymentInfo } =
    useSettingsContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  const handleCloseEdit = () => {
    if (editCheckoutFlow) {
      goBack();
    } else {
      closeModal();
    }
  };

  const handleSaveEdit = async (updatedDetails: BillingDetails) => {
    try {
      await billingDetailsApi.update(updatedDetails);
      updateBillingDetails(updatedDetails);

      if (editCheckoutFlow) {
        openModal('checkout');
      } else {
        closeModal();
      }
    } catch (error) {
      console.error('Failed to save billing details:', error);
      throw error;
    }
  };

  const handleSubscribe = async () => {
    // Refetch payment info so UI reflects the new subscription state
    await refetchPaymentInfo();
    openModal('subscription-success');
  };

  const handleSubscribeNowToEditDetails = (billingCycle: 'annual' | 'monthly') => {
    // If billing details haven't loaded, proceed with empty form (edit modal will prefill)
    openModal('edit', { checkoutFlow: true, billingCycle });
  };

  // DISABLED: Stripe billing portal — payments handled by private invoices.
  // Trial days managed via invitation codes only.
  // const handlePaymentMethodClick = async () => {
  //   try {
  //     const { url } = await paymentsApi.createPortalSession();
  //     window.open(url, '_blank');
  //     closeModal();
  //   } catch (error) {
  //     console.error('Failed to open billing portal:', error);
  //   }
  // };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <AnimatePresence mode="sync" custom={direction}>
      {currentModal === 'edit' && (
        <ModalWrapper
          key="edit-modal"
          custom={direction}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={slideVariants}
        >
          <ModalContent>
            <EditDetailsModal
              open={true}
              initial={
                billingDetails || {
                  firstName: '',
                  lastName: '',
                  companyName: '',
                  streetAddress: '',
                  city: '',
                  stateProvince: '',
                  postalCode: '',
                  country: '',
                  vatTaxId: '',
                  billingEmail: '',
                }
              }
              onClose={handleCloseEdit}
              onSave={handleSaveEdit}
              checkoutFlow={editCheckoutFlow}
              profile={profile}
            />
          </ModalContent>
        </ModalWrapper>
      )}

      {currentModal === 'checkout' && (
        <ModalWrapper
          key="checkout-modal"
          custom={direction}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={slideVariants}
        >
          <ModalContent>
            <CheckoutModal
              open={true}
              onClose={goBack}
              onSubscribe={handleSubscribe}
              planName={selectedPlanName || planName}
              billingCycle={selectedBillingCycle}
            />
          </ModalContent>
        </ModalWrapper>
      )}

      {currentModal === 'subscribe-now' && (
        <ModalWrapper
          key="subscribe-now-modal"
          custom={direction}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={slideVariants}
        >
          <ModalContent>
            <SubscribeNowModal
              open={true}
              onClose={closeModal}
              onNavigateToEditDetails={handleSubscribeNowToEditDetails}
              planName={planName}
              trialEnd={trialEnd || ''}
            />
          </ModalContent>
        </ModalWrapper>
      )}

      {currentModal === 'subscription-success' && (
        <ModalWrapper
          key="subscription-success-modal"
          custom={direction}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={slideVariants}
        >
          <ModalContent>
            <SubscriptionSuccessModal open={true} onClose={closeModal} />
          </ModalContent>
        </ModalWrapper>
      )}

      {currentModal === 'payment-method' && (
        <ModalWrapper
          key="payment-method-modal"
          custom={direction}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={slideVariants}
        >
          {/* <ModalContent>
          <PaymentMethodModal
            open={true}
            onClose={goBack}
            onManageSubscription={handlePaymentMethodClick}
          />
          </ModalContent> */}
        </ModalWrapper>
      )}
    </AnimatePresence>
  );
}
